import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  simpleProccessStatusCalc,
  SimpleProcessStatus as SimpleStatus,
} from '../../../utils/status';
import type { LazadaArgueOrderStatus, ShopeeArgueOrdertatus } from '../../../utils/status';
import type { RefundDrawerDataSource, RefundDrawerStatusTagData } from '../types';

interface UseRefundDrawerStatusParams {
  dataSource: RefundDrawerDataSource | null;
  platform: General.Platform;
}

/**
 * 根据平台与 returnStatus 计算简化状态与标题栏 Tag 展示数据。
 * 仅返回可读状态与 Tag 文案/类型，不包含 JSX。
 */
export function useRefundDrawerStatus({ dataSource, platform }: UseRefundDrawerStatusParams): {
  statusTagData: RefundDrawerStatusTagData | null;
  simpleStatus: SimpleStatus;
} {
  const { t } = useTranslate();

  return useMemo(() => {
    const defaultSimpleStatus = SimpleStatus.Processing;
    if (!dataSource) {
      return { statusTagData: null, simpleStatus: defaultSimpleStatus };
    }

    const statusRaw = dataSource.returnStatus as LazadaArgueOrderStatus | ShopeeArgueOrdertatus;
    const finalStatus = simpleProccessStatusCalc(platform, statusRaw);
    let tagType: 'success' | 'processing' | 'warning' =
      finalStatus.simpleStatus === SimpleStatus.Done ? 'success' : 'processing';
    if (finalStatus.simpleStatus === SimpleStatus.Pending) tagType = 'warning';

    if (platform === 'lazada') {
      const status = statusRaw as LazadaArgueOrderStatus;
      const tagText = t(`lazada.${status}`);
      return {
        statusTagData: { tagType, tagText },
        simpleStatus: finalStatus.simpleStatus,
      };
    }

    return { statusTagData: null, simpleStatus: finalStatus.simpleStatus };
  }, [dataSource, platform, t]);
}
